import { Injectable } from '@angular/core';
import { deriveAppOrigin } from '../_helpers/derive-origin';
import { OrgCssThemeEntry } from '../_interface/company';
import { AppTheme, ThemeService } from './theme.service';

/**
 * Vrednost podešavanja `theme` koja bira temu organizacije: `org:<ThemeID>` (ostale vrednosti su
 * ugrađene šeme). ⚠ SYNC: BackOffice `OrgCssService.ThemeRefPrefix`, admin `device-settings-form`.
 */
export const ORG_THEME_PREFIX = 'org:';
export type OrgThemeRef = `org:${number}`;

/** Vrednost podešavanja `theme` (ugrađena šema ili tema organizacije). */
export type ThemeSetting = AppTheme | OrgThemeRef;

export function isOrgThemeRef(value: unknown): value is OrgThemeRef {
  return typeof value === 'string' && value.startsWith(ORG_THEME_PREFIX);
}

/** Razrešen izbor teme: šema za ThemeService (null = podrazumevana šema aplikacije) i verzija CSS-a. */
export interface ResolvedTheme {
  scheme: AppTheme | null;
  /** Hash CSS-a teme; prazno = bez org CSS-a. `undefined` = ne diraj CSS (katalog nije stigao). */
  version: string | undefined;
}

/** Šeme koje tema organizacije sme da nadogradi (bez `auto`). ⚠ SYNC: BackOffice `OrgCssService.BaseSchemes`. */
const ORG_THEME_SCHEMES: AppTheme[] = ['plum', 'daylight', 'blue', 'contrast', 'warm'];

/** `<link>` sa objavljenom temom. Isti id koristi i pre-boot skripta u index.html. */
const LINK_ID = 'org-custom-css';
/** Draft iz admin editora — zaseban `<style>`, da ne gazi objavljeni `<link>` istog imena. */
const PREVIEW_STYLE_ID = 'org-custom-css-preview';
/** Marker na `<link>`-u koji se još učitava (swap-on-load). */
const PENDING_ATTR = 'data-org-css-pending';

/* ⚠ SYNC: ključeve i verziju ispod koristi i pre-boot skripta u index.html svake app
 *  koja primenjuje org CSS (kiosk/waiter/KD/orders) — menjaš li ovde, promeni i tamo. */
/** Verzija formata keša. v3 = pokazivač (hash), ne više sam CSS — promena invalidira starije ključeve. */
const CACHE_VERSION = 'v3';
const CACHE_PREFIX = 'orgCustomCss.';
/** Companion ključ: aktivni tenant, da pre-boot zna KOJI pokazivač sme da učita. */
const TENANT_KEY = 'orgCustomCss.tenant';
/** '1' = prošli boot je uspešno stigao do primene sveže teme → pre-boot sme da koristi pokazivač.
 *  Pre-boot ga spušta na '0' čim okači `<link>` (watchdog): boot koji se ne završi ostavlja '0',
 *  pa sledeći boot preskače keš i diže se čist. */
const BOOT_OK_KEY = 'orgCustomCss.bootOk';
/** Query/hash escape hatch za servisere: ?noOrgCss=1 → tema se ne primenjuje, keš se briše. */
const DISABLE_PARAM = 'noOrgCss';
/** Šema izabrane teme organizacije (`{ ref: 'org:5', scheme: 'daylight' }`) — pre-boot skripta
 *  podešavanja `theme` = `org:<id>` ne ume sama da razreši, pa čita šemu odavde (bez treptaja), a
 *  `applySelection` je koristi dok katalog ne stigne. `ref` čuva od zastarelog keša: važi samo dok
 *  je u podešavanjima izabrana ista tema. */
const SCHEME_KEY = 'orgCustomCss.scheme';

/* ⚠ SYNC: preview protokol deli admin portal — `organization/app-css/app-css.component.ts`. */
/** admin → app: nesnimljen CSS iz editora + osnovna šema teme (`{ type, css, scheme? }`). */
const PREVIEW_MESSAGE = 'orgCssPreview';
/** app → admin: listener je živ, pošalji trenutni draft (Angular boot je async, pa admin ne
 *  sme da pretpostavi da je iframe `load` = app spremna). */
const PREVIEW_READY_MESSAGE = 'orgCssPreviewReady';
/** Poddomen admin portala — jedini dozvoljen pošiljalac preview poruka. */
const ADMIN_SUBDOMAIN = 'admin';

/**
 * Primena tema organizacije (org CSS).
 *
 * Tema = naziv + ugrađena šema boja + CSS koji je samo nadogradnja te šeme. Organizacija ih pravi u
 * admin portalu (organization/themes); uređaj bira temu kroz svoje podešavanje `theme`, gde vrednost
 * `org:<id>` znači temu organizacije, a ostale vrednosti ugrađene šeme (podrazumevanu temu po
 * aplikaciji određuje profil uređaja). Katalog tema stiže kroz payload — kiosk kroz
 * `menu.settings.organizationCssThemes` (public menu payload), a waiter/KD/orders kroz
 * `CompanySettings.organizationCssThemes` — i nosi po temi samo šemu i **verziju** (hash) CSS-a.
 * `applySelection` iz izbora i kataloga postavi šemu (ThemeService) i okači CSS teme; uređaj na
 * ugrađenoj šemi nema org CSS.
 *
 * Unutar teme CSS i dalje ima "global" sekciju (sve app-e) + per-app pravila pod
 * `html[data-app="<tip>"]`; server ga čuva kao fajl na blob-u pod imenom koje je hash sadržaja.
 *
 * Sam CSS se učitava kao `<link href="/api/organization/css/{companyID}/{version}.css">`, pa ga service worker
 * kešira cache-first — radi i offline.
 *
 * ⚠ `orgCss` dataGroup u `ngsw-config.json` MORA stajati PRE `noCache` grupe. Angular SW bira PRVU
 * grupu koja se poklopi, a `noCache` hvata ceo `/api` prefiks (`strategy: freshness, maxAge: 0u`) —
 * iza nje bi tema bila network-first i pucala bi offline. JSON ne trpi komentare, pa ograničenje
 * stoji ovde. Telo CSS-a namerno više
 * ne putuje kroz payload: sa ugrađenim (data:) slikama ume da bude stotine kilobajta, a stizalo je uz
 * svaki odgovor. U localStorage-u ostaje samo pokazivač (~40 bajta), koji pre-boot skripta koristi da
 * okači `<link>` pre prvog paint-a (bez treptaja/FOUC).
 *
 * Ovo je JEDINI mehanizam za boju/izgled (zamenjuje legacy `CompanySettings.primaryColor`): boje se
 * postavljaju override-om `--app-*` tokena (npr. `html[data-app="kiosk"] { --app-brand: #... }`), a most
 * `--primary: var(--app-brand)` u kiosku znači da ceo UI prati `--app-brand`.
 *
 * Bez sečenja po app-u — `data-app` na <html> filtrira koja pravila stvarno važe (nematched
 * `[data-app]` pravila su inertna), pa jedan isti fajl služi sve aplikacije organizacije.
 */
@Injectable({ providedIn: 'root' })
export class OrgThemeService {

  /** Kill-switch uhvaćen pri konstrukciji servisa (rani bootstrap, URL još netaknut):
   *  router/app posle boot-a ume da PREPIŠE URL i izbaci query, pa bi provera samo u trenutku
   *  applyTheme promašila parametar — a payload bi vratio pokvarenu temu koju je pre-boot upravo
   *  preskočio. Važi ceo boot; reload bez parametra vraća normalno ponašanje. */
  private readonly disabledAtBoot: boolean = this.isDisabled();

  /** true od prve preview poruke — draft iz admin editora od tad drži prikaz, pa objavljena tema
   *  (menu reload, `companySettingsChanged` WS) ne sme da ga pregazi. Ne resetuje se:
   *  preview okvir/prozor živi koliko i sesija editora. */
  private previewActive = false;

  constructor(private readonly theme: ThemeService) {
    this.startPreviewListener();
  }

  /**
   * Razreši vrednost podešavanja `theme` uz katalog tema organizacije.
   *
   *  - ugrađena šema → ta šema, bez org CSS-a;
   *  - `org:<id>` iz kataloga → šema teme + verzija njenog CSS-a;
   *  - `org:<id>` kog nema u katalogu (obrisana tema) → podrazumevana šema aplikacije, bez CSS-a;
   *  - `org:<id>` a katalog je IZOSTAO (`undefined`, stariji server / payload bez polja) → šema i CSS
   *    se ne diraju (`scheme: null`, `version: undefined`) — ne skidaj temu zbog nepotpunog payloada.
   */
  resolve(value: string | null | undefined, catalog: OrgCssThemeEntry[] | null | undefined): ResolvedTheme {
    if (!isOrgThemeRef(value)) {
      return { scheme: (value || null) as AppTheme | null, version: '' };
    }
    if (catalog === undefined) {
      return { scheme: null, version: undefined };
    }
    const id = Number(value.substring(ORG_THEME_PREFIX.length));
    const entry = (catalog || []).find(t => t.id === id);
    if (!entry) {
      return { scheme: null, version: '' };
    }
    const scheme = ORG_THEME_SCHEMES.includes(entry.scheme as AppTheme) ? entry.scheme as AppTheme : null;
    return { scheme, version: entry.version || '' };
  }

  /**
   * Primeni izbor teme uređaja: šemu (ThemeService) i CSS teme (`applyTheme`). Zove se na svaku promenu
   * podešavanja i na svaki nov katalog (payload / `companySettingsChanged`) — idempotentno: ista šema
   * i ista verzija ne diraju ništa.
   *
   * Dok katalog ne stigne (`undefined`), a izabrana je tema organizacije, važi keširana šema iste
   * teme, a CSS se ne dira. Dok traje živi prikaz iz admin editora ne radi ništa — prikaz drži šemu
   * i CSS iz editora. Vraća razrešen izbor (npr. da aplikacija zna da li tema nosi CSS).
   */
  applySelection(
    value: string | null | undefined,
    catalog: OrgCssThemeEntry[] | null | undefined,
    tenantID?: number | string | null,
  ): ResolvedTheme {
    const resolved = this.resolve(value, catalog);
    if (this.previewActive) {
      return resolved;
    }

    // Katalog izostao, a izabrana je tema organizacije: ne diraj keš ni CSS (v. `resolve`). Šema je
    // ona koju je keš zapamtio za isti izbor — pre-boot skripta je već postavila nju, ali ThemeService
    // pri startu vrati podrazumevanu, a katalog (company podešavanja) stiže tek posle prijave ili
    // mrežom, offline nikad.
    const keepScheme = isOrgThemeRef(value) && catalog === undefined;
    if (keepScheme) {
      const cached = this.cachedScheme(value);
      if (cached && cached !== this.theme.current) {
        this.theme.set(cached);
      }
    } else {
      const scheme = resolved.scheme ?? this.theme.defaultTheme;
      if (scheme !== this.theme.current) {
        this.theme.set(scheme);
      }
      this.cacheScheme(isOrgThemeRef(value) && resolved.scheme ? { ref: value, scheme: resolved.scheme } : null);
    }

    this.applyTheme(resolved.version, tenantID);
    return resolved;
  }

  /**
   * Primeni org temu iz payloada i zapamti pokazivač (idempotentno — isti `<link>` se ne dira).
   *
   * Semantika vrednosti: `undefined` = polje IZOSTALO iz payloada (stariji server / parcijalan
   * endpoint) → ne diraj postojeće stanje; `null`/`''` = organizacija nema temu (ili je suspendovana)
   * → skini stil i pokazivač. `tenantID` je companyID iz payloada — bez njega se URL ne može sastaviti,
   * pa nema šta ni da se primeni.
   */
  applyTheme(version: string | null | undefined, tenantID?: number | string | null): void {
    if (this.disabledAtBoot || this.isDisabled()) {
      // Escape hatch (?noOrgCss=1): skini sve i počisti keš da se pokvarena tema ne vrati.
      this.setLink('');
      this.setPreviewStyle('');
      this.purgeCache();
      return;
    }
    if (this.previewActive) {
      return; // draft iz admin editora je autoritet dok traje preview
    }
    if (version === undefined) {
      return;
    }

    const tenant = this.tenantOf(tenantID);
    const value = (version ?? '').trim();

    if (!value) {
      this.setLink('');
      this.cacheVersion('', tenant);
      return;
    }

    if (!tenant) {
      // Bez companyID nema URL-a. Ne bi trebalo da se desi (payload uvek nosi firmu), ali neka se ZNA.
      this.report('applyTheme', new Error('org CSS verzija bez companyID — tema se ne primenjuje'));
      return;
    }

    this.setLink(this.cssUrl(tenant, value));
    this.cacheVersion(value, tenant);
  }

  /** Boot je stigao do sveže teme → pre-boot sme da koristi pokazivač pri sledećem paljenju. */
  markBootOk(): void {
    try {
      localStorage.setItem(BOOT_OK_KEY, '1');
    } catch (err) {
      this.report('markBootOk', err);
    }
  }

  /** Logout / istek sesije / deprovizija: skini temu i obriši SAV org-CSS keš.
   *  Uređaj sme da pređe drugoj organizaciji — ne ostavljaj tuđi brending. */
  clear(): void {
    this.setLink('');
    this.purgeCache();
  }

  /** Fallback: postavi `data-app` na <html> ako pre-boot skripta to nije već uradila. */
  ensureAppAttribute(appType: string): void {
    try {
      const el = document.documentElement;
      if (appType && !el.getAttribute('data-app')) {
        el.setAttribute('data-app', appType);
      }
    } catch {
      /* non-browser / SSR — ignore */
    }
  }

  /**
   * Live preview org CSS-a: kad app radi u okviru ili u prozoru koji je otvorio admin portal
   * (organization/themes), svaki otkucaj u editoru stiže postMessage-om i odmah se ubrizgava —
   * org vidi izmene bez snimanja i bez reload-a u drugom browseru.
   *
   * Tri zaštite koje ne smeju da otpadnu:
   *  1. Poruka se prihvata SAMO iz očekivanog admin origin-a i SAMO kad app ima okvir ili
   *     opener — ista provera kao u `CrossDomainStorageService` (`event.origin !== base` → ignore).
   *  2. Draft se NE kešira. Pokazivač u localStorage-u čita pre-boot skripta, a taj origin deli sa
   *     pravim uređajem/tabom — nesnimljena tema tamo ne sme da završi.
   *  3. Objavljeni `<link>` se GASI dok traje preview. Draft je `<style>` posle njega pa pobeđuje pri
   *     jednakoj specifičnosti, ali pravilo koje je org upravo OBRISAO iz draft-a bi i dalje važilo
   *     iz fajla — org ne bi video brisanje.
   */
  private startPreviewListener(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      // Dva režima: app UOKVIRENA u editoru (`parent`) i app otvorena kao ZASEBAN PROZOR iz
      // editora (`opener`). Popup je top-level prozor, pa mu je `window.parent === window` —
      // bez opener grane živi prikaz u novom prozoru nikad ne bi dobio ni jednu poruku.
      const host: Window | null = window.parent !== window ? window.parent : window.opener;
      if (!host) {
        return; // ni okvir ni popup — nema ko da šalje preview
      }

      const adminOrigin = deriveAppOrigin(ADMIN_SUBDOMAIN);
      if (!adminOrigin) {
        return; // same-origin režim (LocalServer / localhost) — nema admin poddomena
      }

      window.addEventListener('message', (event: MessageEvent) => {
        if (event.origin !== adminOrigin || !event.data || event.data.type !== PREVIEW_MESSAGE) {
          return;
        }
        this.previewActive = true;
        this.disableLink();
        this.setPreviewStyle(String(event.data.css ?? '').trim());
        // Tema je nadogradnja svoje šeme — prikaz mora da je pokaže na toj šemi, ne na šemi
        // podešavanja ovog browsera.
        const scheme = event.data.scheme as AppTheme;
        if (ORG_THEME_SCHEMES.includes(scheme) && scheme !== this.theme.current) {
          this.theme.set(scheme);
        }
      });

      host.postMessage({ type: PREVIEW_READY_MESSAGE }, adminOrigin);
    } catch (err) {
      this.report('startPreviewListener', err);
    }
  }

  /** Javni URL teme. Isti fajl služi sve app-e organizacije — `data-app` filtrira šta važi. */
  private cssUrl(tenant: string, version: string): string {
    return `/api/organization/css/${encodeURIComponent(tenant)}/${encodeURIComponent(version)}.css`;
  }

  /**
   * Postavi `<link>` sa temom. Prazan href = skini temu.
   *
   * Swap-on-load: novi `<link>` se okači, a stari skida tek kad je novi učitan (ili pukao). Bez toga
   * SPA navigacija restoran→restoran ima prozor u kom nema nijednog stila. Zato i marker
   * `data-org-css-pending`: mora preživeti TAČNO jedan link, jer bi dva značila stopljene teme dve
   * organizacije — kvar koji se ne vidi kao greška, nego kao pogrešan brending.
   */
  private setLink(href: string): void {
    try {
      // Nedovršen swap iz prethodnog poziva (brza navigacija A→B→C) — odbaci ga.
      const pending = document.querySelectorAll('link[' + PENDING_ATTR + ']');
      for (let i = 0; i < pending.length; i++) {
        pending[i].remove();
      }

      const current = document.getElementById(LINK_ID) as HTMLLinkElement | null;

      if (!href) {
        if (current) {
          current.remove();
        }
        return;
      }

      if (current) {
        current.disabled = false;
        if (current.getAttribute('href') === href) {
          // Ista verzija — nema ponovnog fetch-a, ali POZICIJA mora da se ispravi:
          // pre-boot skripta iz index.html okači ovaj `<link>` dok se parsira <head>,
          // dakle PRE Angular-ovog `styles.*.css`. Tema (`html.theme-<key>`) i org
          // scaffold (`html[data-app="<tip>"]`) imaju ISTU specifičnost (0,1,1), pa pri
          // remiju pobeđuje kasniji čvor — org `--app-*` override-i bi tiho otpali i
          // app bi se digla polovično obojena (pravila bez parnjaka u temi rade,
          // tokeni ne). appendChild na već okačen čvor ga premešta na kraj <head>
          // bez novog zahteva. Isti razlog kao kod setPreviewStyle.
          document.head.appendChild(current);
          return;
        }
      }

      const next = document.createElement('link');
      next.rel = 'stylesheet';
      next.setAttribute(PENDING_ATTR, '');

      const settle = () => {
        if (current && current.parentNode) {
          current.remove();
        }
        next.removeAttribute(PENDING_ATTR);
        next.id = LINK_ID;
      };

      next.addEventListener('load', settle);
      next.addEventListener('error', settle);
      next.href = href;

      document.head.appendChild(next);
    } catch (err) {
      this.report('setLink', err);
    }
  }

  /** Ugasi objavljenu temu bez skidanja iz DOM-a — koristi preview, v. startPreviewListener. */
  private disableLink(): void {
    try {
      const link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
      if (link) {
        link.disabled = true;
      }
    } catch (err) {
      this.report('disableLink', err);
    }
  }

  /** Upsert `<style id="org-custom-css-preview">` i drži ga POSLEDNJIM u <head>. */
  private setPreviewStyle(css: string): void {
    try {
      let el = document.getElementById(PREVIEW_STYLE_ID) as HTMLStyleElement | null;
      if (!el) {
        el = document.createElement('style');
        el.id = PREVIEW_STYLE_ID;
      }
      // appendChild na već-okačeni čvor ga PREMEŠTA na kraj <head>.
      document.head.appendChild(el);
      el.textContent = css || '';
    } catch (err) {
      this.report('setPreviewStyle', err);
    }
  }

  /** Šema izabrane teme organizacije za pre-boot skriptu; null = izabrana je ugrađena šema (keš se briše). */
  private cacheScheme(entry: { ref: string; scheme: AppTheme } | null): void {
    try {
      if (entry) {
        localStorage.setItem(SCHEME_KEY, JSON.stringify(entry));
      } else {
        localStorage.removeItem(SCHEME_KEY);
      }
    } catch (err) {
      this.report('cacheScheme', err);
    }
  }

  /** Keširana šema teme organizacije, samo dok je u podešavanjima izabrana ista tema (`ref`). */
  private cachedScheme(ref: string): AppTheme | null {
    try {
      const entry = JSON.parse(localStorage.getItem(SCHEME_KEY) || 'null');
      return entry && entry.ref === ref && ORG_THEME_SCHEMES.includes(entry.scheme) ? entry.scheme : null;
    } catch (err) {
      this.report('cachedScheme', err);
      return null;
    }
  }

  private cacheVersion(version: string, tenant: string): void {
    try {
      this.removeLegacyKeys();
      if (!tenant) {
        return; // bez tenant-a nema bezbednog ključa
      }
      const key = CACHE_PREFIX + CACHE_VERSION + '.' + tenant;
      if (version) {
        localStorage.setItem(key, version);
        localStorage.setItem(TENANT_KEY, tenant);
      } else {
        localStorage.removeItem(key);
        // Obrisana tema ⇒ skini i companion pointer ako pokazuje na ovaj tenant — zaostali pointer
        // bi na sledećem boot-u naveo pre-boot skriptu da okači `<link>` koji više ne važi.
        if (localStorage.getItem(TENANT_KEY) === tenant) {
          localStorage.removeItem(TENANT_KEY);
        }
      }
    } catch (err) {
      this.report('cacheVersion', err);
    }
  }

  /** Obriši sve `orgCustomCss*` ključeve (sve verzije, sve tenant-e, companion + bootOk + legacy). */
  private purgeCache(): void {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf('orgCustomCss') === 0) {
          doomed.push(key);
        }
      }
      doomed.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      this.report('purgeCache', err);
    }
  }

  /** Higijena: ključevi ranijih verzija mehanizma (v1 nenamespace-ovan, v2 je čuvao ceo CSS). */
  private removeLegacyKeys(): void {
    try {
      localStorage.removeItem('orgCustomCss');

      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf(CACHE_PREFIX + 'v2.') === 0) {
          doomed.push(key);
        }
      }
      doomed.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      this.report('removeLegacyKeys', err);
    }
  }

  private tenantOf(tenantID?: number | string | null): string {
    return tenantID === null || tenantID === undefined ? '' : String(tenantID).trim();
  }

  /** `?noOrgCss=1` (query ili hash) — servisni izlaz kad objavljena tema polomi UI.
   *  Case-insensitive: waiter posle boot-a lowercase-uje URL, pa bi provera tačnog
   *  zapisa promašila `?noorgcss` iz već-normalizovanog URL-a. */
  private isDisabled(): boolean {
    try {
      return (window.location.search + window.location.hash)
        .toLowerCase()
        .indexOf(DISABLE_PARAM.toLowerCase()) !== -1;
    } catch {
      return false;
    }
  }

  /** Telemetrija umesto tihog gutanja — apply/cache fail ne sme da bude nevidljiv. */
  private report(context: string, err: unknown): void {
    try {
      console.warn('[OrgThemeService] ' + context + ' failed:', err);
    } catch {
      /* ignore */
    }
  }
}
